import { useRef, useEffect, useState, CSSProperties } from 'react';
import { motion } from 'framer-motion';

const Register = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [currentDate] = useState(new Date());
  const [regiaoAtual, setRegiaoAtual] = useState<string>("");
  const [testMode, setTestMode] = useState(false);
  
  // Links de inscrição específicos por região
  const inscricaoLinks = {
    APAC: "https://app.smartsheet.com/b/form/87c47929ab264d98bc4c4db75461b35e",
    APS: "https://app.smartsheet.com/b/form/6b910ef28703490692b5556b9c7e9ac0",
    NORDESTE: "https://app.smartsheet.com/b/form/b125c348d060496e8e3815eed0374bf3",
    default: "#register" // Link padrão caso nenhuma região seja detectada
  };
  
  // Definindo datas de início e fim do período de inscrição
  const startRegistrationDate = new Date('2025-04-14T00:00:00');
  const endRegistrationDate = new Date('2025-05-09T23:59:59');
  
  // Verificando se estamos no período de inscrição
  const isRegistrationPeriod = currentDate >= startRegistrationDate && currentDate <= endRegistrationDate;
  const isBeforeRegistration = currentDate < startRegistrationDate;
  const isAfterRegistration = currentDate > endRegistrationDate;
  
  // Definindo textos conforme o período
  let titleText = "Pronto para o desafio?";
  let descriptionText = "Clique no botão abaixo para acessar o formulário de inscrição e garantir a participação da sua equipe no Hackathon Happy de 2025.";
  let buttonText = "Inscreva-se";
  
  if (isBeforeRegistration) {
    titleText = "Inscrições em breve!";
    descriptionText = `As inscrições para o Hackathon Happy 2025 serão abertas em 14/04/2025. Fique atento e não perca essa oportunidade!`;
    buttonText = "Inscrições não iniciadas";
  } else if (isAfterRegistration) {
    titleText = "Inscrições encerradas!";
    descriptionText = "O período de inscrições para o Hackathon Happy 2025 foi encerrado em 09/05/2025. Agradecemos o interesse e esperamos vê-lo na próxima edição!";
    buttonText = "Inscrições encerradas";
  }

  // Verificar se o modo de teste está ativo via URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('test_mode') || urlParams.has('teste')) {
      setTestMode(true);
    }
  }, []);

  // Obter a região atual do atributo data-regiao
  useEffect(() => {
    const detectarRegiao = () => {
      const regiao = document.documentElement.getAttribute('data-regiao');
      if (regiao) {
        setRegiaoAtual(regiao.toUpperCase());
      }
    };

    detectarRegiao();

    // Observar mudanças no atributo data-regiao
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' && 
          mutation.attributeName === 'data-regiao'
        ) {
          detectarRegiao();
        }
      });
    });
    
    // Iniciar observação
    observer.observe(document.documentElement, { attributes: true });
    
    // Limpeza ao desmontar
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const currentRef = ref.current;
    
    // Configurar o observer para detectar quando o componente está visível
    let observer: IntersectionObserver | null = null;
    
    // Pequeno atraso para evitar problemas de timing
    const timer = setTimeout(() => {
      if (currentRef) {
        // Definir um threshold menor para dispositivos móveis
        const isMobile = window.innerWidth < 768;
        const threshold = isMobile ? 0.05 : 0.3;
        
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            setIsInView(entry.isIntersecting);
          },
          { 
            threshold: threshold,
            rootMargin: isMobile ? '150px 0px' : '0px'
          }
        );
        
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Função para obter o link de inscrição com base na região atual
  const getInscricaoLink = (): string => {
    if (regiaoAtual === "APAC") {
      return inscricaoLinks.APAC;
    } else if (regiaoAtual === "APS") {
      return inscricaoLinks.APS;
    } else if (regiaoAtual === "NORDESTE") {
      return inscricaoLinks.NORDESTE;
    } else {
      return inscricaoLinks.default;
    }
  };

  const sectionStyle: CSSProperties = {
    padding: '7rem 0',
    background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.03) 0%, rgba(255, 255, 255, 0.8) 100%)',
    overflow: 'hidden',
    position: 'relative',
  };

  const bgPatternStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'radial-gradient(rgba(37, 99, 235, 0.1) 1px, transparent 1px)',
    backgroundSize: '30px 30px',
    opacity: 0.5,
    pointerEvents: 'none',
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1.5rem',
    position: 'relative',
    zIndex: 2,
  };

  const cardStyle: CSSProperties = {
    width: '100%',
    maxWidth: '45rem', // Reduzindo a largura já que teremos apenas uma seção
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '1.5rem',
    overflow: 'hidden',
    boxShadow: '0 20px 50px -12px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(37, 99, 235, 0.1)',
  };

  const actionBoxStyle: CSSProperties = {
    padding: '3.5rem',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '1',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  };

  const actionHeadingStyle: CSSProperties = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: '1rem',
    textAlign: 'center',
    position: 'relative',
    display: 'inline-block',
  };

  const actionHeadingLineStyle: CSSProperties = {
    position: 'absolute',
    bottom: '-0.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '50px',
    height: '3px',
    background: 'linear-gradient(to right, #2563eb, #60a5fa)',
    borderRadius: '4px',
  };

  const actionTextStyle: CSSProperties = {
    fontSize: '1.125rem',
    color: '#64748b',
    marginBottom: '2.5rem',
    textAlign: 'center',
    maxWidth: '85%',
    lineHeight: 1.7,
  };

  const buttonStyle: CSSProperties = {
    backgroundColor: isHovering ? '#1d4ed8' : '#2563eb',
    color: 'white',
    padding: '1.25rem 2.75rem',
    borderRadius: '0.75rem',
    fontSize: '1.125rem',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: isHovering 
      ? '0 10px 25px -5px rgba(37, 99, 235, 0.5), 0 8px 10px -6px rgba(37, 99, 235, 0.2)'
      : '0 10px 15px -3px rgba(37, 99, 235, 0.2), 0 4px 6px -2px rgba(37, 99, 235, 0.1)',
    textDecoration: 'none',
    display: 'inline-block',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
  };

  const buttonGlowStyle: CSSProperties = {
    position: 'absolute',
    width: '200%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    top: 0,
    left: isHovering ? '100%' : '-100%',
    transition: 'left 0.6s ease',
    zIndex: -1,
  };

  const titleContainerStyle: CSSProperties = {
    textAlign: 'center',
    marginBottom: '2rem',
  };

  const titleStyle: CSSProperties = {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: '#1e40af',
    fontFamily: 'Kobe, sans-serif'
  };

  const titleUnderlineStyle: CSSProperties = {
    width: '50px',
    height: '3px',
    background: 'linear-gradient(to right, #2563eb, #60a5fa)',
    borderRadius: '4px',
    margin: '0 auto',
  };

  const descriptionStyle: CSSProperties = {
    fontSize: '1.125rem',
    color: '#64748b',
    textAlign: 'center',
    marginBottom: '2rem',
  };

  const buttonContainerStyle: CSSProperties = {
    textAlign: 'center',
  };

  return (
    <section id="register" style={sectionStyle} ref={ref}>
      <div style={bgPatternStyle}></div>
      <div style={containerStyle}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          style={cardStyle}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={actionBoxStyle}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              style={titleContainerStyle}
            >
              <h2 style={titleStyle} className="title-font">{titleText}</h2>
              <div style={titleUnderlineStyle}></div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              style={descriptionStyle}
            >
              {descriptionText}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              style={buttonContainerStyle}
            >
              <motion.a
                href={(isRegistrationPeriod || testMode) ? getInscricaoLink() : undefined}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...buttonStyle,
                  backgroundColor: (isRegistrationPeriod || testMode) ? "#ffb03f" : "#a0a0a0",
                  cursor: (isRegistrationPeriod || testMode) ? "pointer" : "not-allowed",
                }}
                whileHover={(isRegistrationPeriod || testMode) ? { scale: 1.05 } : {}}
                whileTap={(isRegistrationPeriod || testMode) ? { scale: 0.95 } : {}}
                onClick={(e) => !(isRegistrationPeriod || testMode) && e.preventDefault()}
                title={(isRegistrationPeriod || testMode) ? "" : (isBeforeRegistration ? "O período de inscrições ainda não começou" : "Período de inscrições encerrado")}
              >
                {buttonText}
                <div style={buttonGlowStyle}></div>
              </motion.a>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Register; 